import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence, useMotionValue, useSpring } from 'framer-motion'
import { RARE_EARTH_ELEMENTS } from '@/data/rareEarthElements'
import type { RareEarthElement } from '@/data/rareEarthElements'

const popFromNavVariants = {
  enter: {
    y: 40,
    scale: 0.15,
    opacity: 0,
  },
  center: {
    y: 0,
    scale: 1,
    opacity: 1,
    transition: {
      type: 'spring' as const,
      stiffness: 260,
      damping: 24,
      staggerChildren: 0.05,
      delayChildren: 0.06,
    },
  },
  exit: {
    y: 30,
    scale: 0.2,
    opacity: 0,
    transition: { duration: 0.2 },
  },
}

const staggerItem = {
  enter: { opacity: 0, y: 8 },
  center: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring' as const, stiffness: 400, damping: 30 },
  },
}

const squareContainerVariants = {
  enter: { opacity: 0 },
  center: {
    opacity: 1,
    transition: { staggerChildren: 0.04, delayChildren: 0.02 },
  },
}

const infoContainerVariants = {
  enter: { opacity: 0 },
  center: {
    opacity: 1,
    transition: { staggerChildren: 0.03, delayChildren: 0.04 },
  },
}

const LIGHT_ELEMENTS = RARE_EARTH_ELEMENTS.filter((e) => e.category === 'light')
const HEAVY_ELEMENTS = RARE_EARTH_ELEMENTS.filter((e) => e.category === 'heavy')

export default function RareEarthElements() {
  const [activeIndex, setActiveIndex] = useState(0)
  const element = RARE_EARTH_ELEMENTS[activeIndex]
  const sectionRef = useRef<HTMLElement>(null)
  const [isInView, setIsInView] = useState(false)

  useEffect(() => {
    const el = sectionRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { rootMargin: '0px 0px -25% 0px', threshold: 0.1 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <section
      ref={sectionRef}
      id="elements"
      className="relative flex flex-col pt-28 md:pt-32 pb-24 md:pb-28 bg-white border-t border-slate-200 overflow-hidden"
    >
      <div className="container relative z-10 flex flex-col">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="text-sm font-semibold text-accent uppercase tracking-wider mb-3">
            Product Portfolio
          </h2>
          <h3 className="text-3xl md:text-4xl font-bold font-heading text-primary tracking-tight mb-4">
            Rare Earth Elements
          </h3>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Lynas produces fifteen separated rare earth elements. Select an element to view its properties and applications.
          </p>
        </motion.div>

        {/* Slide content */}
        <div className="flex justify-center py-4 md:pb-16">
          <div className="w-full max-w-5xl mx-auto relative overflow-visible" style={{ transformOrigin: 'center center' }}>
            <AnimatePresence mode="wait" initial={false}>
              <ElementCard
                key={element.id}
                element={element}
                variants={popFromNavVariants}
              />
            </AnimatePresence>
          </div>
        </div>

        {/* Slider nav - static at bottom on large screens */}
        <div className="hidden md:block pt-12 pb-4 border-t border-slate-200 mt-12">
          <div className="flex flex-wrap justify-center gap-2 md:gap-3">
            {[...LIGHT_ELEMENTS, ...HEAVY_ELEMENTS].map((el) => {
              const i = RARE_EARTH_ELEMENTS.indexOf(el)
              return (
                <motion.button
                  key={el.id}
                  onClick={() => setActiveIndex(i)}
                  whileHover={{ scale: 1.08 }}
                  whileTap={{ scale: 0.95 }}
                  className={`
                    w-12 h-12 md:w-14 md:h-14 rounded-lg border-2 font-bold font-heading text-sm md:text-base
                    transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2
                    ${i === activeIndex
                      ? 'bg-accent border-accent text-white shadow-lg scale-105'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-accent/50 hover:bg-accent/5 hover:text-accent'
                    }
                  `}
                  aria-label={`View ${el.name}`}
                  aria-current={i === activeIndex ? 'true' : undefined}
                >
                  {el.symbol}
                </motion.button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Fixed bottom navigation - mobile only, appears when section in view */}
      <AnimatePresence>
        {isInView && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed bottom-0 left-0 right-0 z-50 p-4 md:hidden bg-white/95 backdrop-blur-md border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
          >
            <div className="container">
              <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2">
                {[...LIGHT_ELEMENTS, ...HEAVY_ELEMENTS].map((el) => {
                  const i = RARE_EARTH_ELEMENTS.indexOf(el)
                  return (
                    <motion.button
                      key={el.id}
                      onClick={() => {
                        setActiveIndex(i)
                        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                      }}
                      whileHover={{ scale: 1.08 }}
                      whileTap={{ scale: 0.95 }}
                      className={`
                        w-9 h-9 sm:w-12 sm:h-12 rounded-lg border-2 font-bold font-heading text-xs sm:text-sm
                        transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2
                        ${i === activeIndex
                          ? 'bg-accent border-accent text-white shadow-lg scale-105'
                          : 'bg-white border-slate-200 text-slate-600 hover:border-accent/50 hover:bg-accent/5 hover:text-accent'
                        }
                      `}
                      aria-label={`View ${el.name}`}
                      aria-current={i === activeIndex ? 'true' : undefined}
                    >
                      {el.symbol}
                    </motion.button>
                  )
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}

interface ElementCardProps {
  element: RareEarthElement
  variants: typeof popFromNavVariants
}

const MAX_TILT = 8

function ElementCard({ element, variants }: ElementCardProps) {
  const cardRef = useRef<HTMLDivElement>(null)
  const rotateX = useMotionValue(0)
  const rotateY = useMotionValue(0)
  const springConfig = { stiffness: 300, damping: 25 }
  const rotateXSpring = useSpring(rotateX, springConfig)
  const rotateYSpring = useSpring(rotateY, springConfig)

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = cardRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width
    const y = (e.clientY - rect.top) / rect.height
    rotateX.set((y - 0.5) * 2 * MAX_TILT)
    rotateY.set((0.5 - x) * 2 * MAX_TILT)
  }

  const handleMouseLeave = () => {
    rotateX.set(0)
    rotateY.set(0)
  }

  return (
    <motion.div
      variants={variants}
      initial="enter"
      animate="center"
      exit="exit"
      style={{ transformOrigin: 'center center' }}
      className="flex flex-col md:flex-row gap-6 md:gap-8 items-center md:items-start"
    >
      {/* Left column: periodic table square */}
      <motion.div variants={staggerItem} className="flex-shrink-0 w-full md:w-auto flex justify-center md:justify-start perspective-1000">
        <motion.div
          ref={cardRef}
          variants={squareContainerVariants}
          className="w-[240px] sm:w-[300px] md:w-[420px] max-w-full aspect-square rounded-[4px] border-2 border-accent bg-white p-4 sm:p-5 md:p-8 flex flex-col shadow-lg cursor-default"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          style={{
            transformStyle: 'preserve-3d',
            rotateX: rotateXSpring,
            rotateY: rotateYSpring,
          }}
        >
          <motion.div
            variants={staggerItem}
            className="flex justify-between items-start"
          >
            <span className="text-accent text-base sm:text-lg md:text-xl font-mono">{element.atomicNumber}</span>
            <span className="text-accent text-base sm:text-lg md:text-xl font-mono">{element.atomicMass}</span>
          </motion.div>
          <div className="flex-1 flex flex-col items-start justify-center">
            <motion.span
              variants={staggerItem}
              className="text-4xl sm:text-5xl md:text-8xl font-bold font-heading text-accent leading-none block"
            >
              {element.symbol}
            </motion.span>
            <motion.span
              variants={staggerItem}
              className="text-accent font-semibold mt-2 sm:mt-3 text-base sm:text-lg md:text-3xl"
            >
              {element.name}
            </motion.span>
          </div>
          <motion.div variants={staggerItem} className="hidden sm:block pt-3">
            <span className="text-accent font-mono text-base sm:text-lg md:text-xl">{element.electronConfig}</span>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Right column: name, description, applications */}
      <motion.div
        variants={infoContainerVariants}
        className="flex-1 min-w-0 bg-white p-6 sm:p-8"
      >
        <motion.h4
          variants={staggerItem}
          className="text-xl font-bold font-heading text-primary mb-4"
        >
          {element.name}
        </motion.h4>
        <motion.p
          variants={staggerItem}
          className="text-slate-600 leading-relaxed mb-6"
        >
          {element.description}
        </motion.p>
        <motion.div
          variants={staggerItem}
          className="pt-6 border-t border-slate-200"
        >
          <h5 className="text-xs font-bold text-accent uppercase tracking-wider mb-3">
            Applications
          </h5>
          <ul className="space-y-2">
            {element.applications.map((app) => (
              <motion.li
                key={app}
                variants={staggerItem}
                className="flex items-start gap-2 text-slate-600"
              >
                <span className="text-accent mt-1.5 w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
                <span>{app}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>
      </motion.div>
    </motion.div>
  )
}
